import { HTTPError } from 'ky'

// Stops at quotes and angle brackets so that redacting a JSON or HTML response
// body leaves it parseable.
const urlPattern = /https?:\/\/[^\s"'<>]+/g

/**
 * The widget's own iframe URL carries both API tokens too, so anything that
 * reports a URL — a request's or the document's — goes through here first.
 */
export const stripQueryString = (url: string): string => url.split(/[?#]/)[0]

const getTokenParam = (url: string): string | undefined =>
  /[?&]token=([^&#]+)/.exec(url)?.[1]

/**
 * Every Co-pilot endpoint carries the API token in the query string, and ky
 * puts the full request URL in `HTTPError.message`. Text derived from a request
 * loses its query strings, plus any bare copy of the token the API echoes back,
 * before it reaches telemetry, storage or the UI.
 */
const redact = (text: string, token?: string): string => {
  const withoutQueryStrings = text.replace(urlPattern, (url) =>
    stripQueryString(url),
  )

  return token
    ? withoutQueryStrings.replaceAll(token, '[redacted]')
    : withoutQueryStrings
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

// Keys the API uses for the human-readable reason a request failed.
const reasonKeys = ['errorMessage', 'message', 'detail', 'title', 'error']

const getFailureReason = (body: unknown): string | undefined => {
  if (typeof body === 'string') return body.trim() || undefined
  if (!isRecord(body)) return undefined

  for (const key of reasonKeys) {
    const value = body[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }

  return undefined
}

const parseJson = (text: string): unknown => {
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}

export const extractHttpError = async (
  error: unknown,
): Promise<RequestError> => {
  if (error instanceof HTTPError) {
    const { name, cause, response } = error
    const token = getTokenParam(error.request.url)
    const message = redact(error.message, token)
    const details = {
      url: stripQueryString(error.request.url),
      errorName: name,
      status: response.status,
      statusText: response.statusText,
    }

    let body: string
    try {
      body = redact(await response.text(), token)
    } catch {
      return new RequestError(message, { ...details, cause })
    }

    const parsedBody = parseJson(body)

    return new RequestError(message, {
      ...details,
      cause: isRecord(parsedBody) ? { cause, ...parsedBody } : cause,
      // A body whose fields are already in `cause` needs no copy here. Anything
      // else — plain text, a proxy's HTML page — would otherwise be lost, and is
      // truncated so it cannot bloat every event and the persisted session.
      responseBody: isRecord(parsedBody)
        ? undefined
        : body.slice(0, 500) || undefined,
      // Only a 4xx reason is copy an agent should read: a 5xx body describes a
      // crash, and `detail` is where exception text lands.
      displayMessage:
        response.status < 500 ? getFailureReason(parsedBody) : undefined,
    })
  }

  if (error instanceof Error) {
    const { name, cause } = error

    return new RequestError(redact(error.message), {
      errorName: name,
      cause,
    })
  }

  if (typeof error === 'string') {
    return new RequestError(redact(error), {})
  }

  return new RequestError('Unknown Error', isRecord(error) ? error : {})
}

export class RequestError extends Error {
  cause: { [key: string]: unknown; status?: number; statusText?: string }

  constructor(
    message: string,
    cause: { [key: string]: unknown; status?: number; statusText?: string },
  ) {
    super(message)
    this.name = 'RequestError'
    this.cause = cause
  }
}
